import { db } from './firebase';
import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  getDocs,
  getDocsFromServer,
  query,
  where,
  orderBy,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';

// Helper function to get docs with server preference in development
const getDocsPreferServer = process.env.NODE_ENV === 'development' ? getDocsFromServer : getDocs;
const getDocPreferServer = process.env.NODE_ENV === 'development' ? getDocFromServer : getDoc;

export interface AdminUser {
  uid: string;
  email: string;
  role: 'super_admin' | 'admin';
  createdAt: any;
  isSuperAdmin: boolean;
}

export interface StreamingProvider {
  id?: string;
  providerId: number;
  providerName: string;
  logoPath: string | null;
  status: 'enabled' | 'disabled' | 'neutral';
  customUrl?: string;
  createdAt: any;
  updatedAt: any;
}

const ADMIN_COLLECTION = 'adminUsers';
const PROVIDERS_COLLECTION = 'streamingProviders';

export const adminService = {
  // Check if super admin exists
  async checkSuperAdminExists(): Promise<boolean> {
    try {
      const q = query(
        collection(db, ADMIN_COLLECTION),
        where('isSuperAdmin', '==', true)
      );
      const snapshot = await getDocsPreferServer(q);
      return !snapshot.empty;
    } catch (error) {
      return false;
    }
  },

  // Create super admin
  async createSuperAdmin(uid: string, email: string): Promise<AdminUser> {
    try {
      const adminUser: AdminUser = {
        uid,
        email,
        role: 'super_admin',
        isSuperAdmin: true,
        createdAt: serverTimestamp(),
      };

      await setDoc(doc(db, ADMIN_COLLECTION, uid), adminUser);
      return adminUser;
    } catch (error) {
      throw error;
    }
  },

  // Check if user is admin
  async isUserAdmin(uid: string): Promise<boolean> {
    try {
      const docRef = doc(db, ADMIN_COLLECTION, uid);
      const docSnap = await getDocPreferServer(docRef);
      return docSnap.exists();
    } catch (error) {
      return false;
    }
  },

  // Get admin user
  async getAdminUser(uid: string): Promise<AdminUser | null> {
    try {
      const docRef = doc(db, ADMIN_COLLECTION, uid);
      const docSnap = await getDocPreferServer(docRef);
      return docSnap.exists() ? docSnap.data() as AdminUser : null;
    } catch (error) {
      return null;
    }
  },

  // Get all streaming providers
  async getAllProviders(): Promise<StreamingProvider[]> {
    try {
      const q = query(
        collection(db, PROVIDERS_COLLECTION),
        orderBy('providerName')
      );
      const snapshot = await getDocsPreferServer(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as StreamingProvider[];
    } catch (error) {
      return [];
    }
  },

  // Get enabled streaming providers
  async getEnabledProviders(): Promise<StreamingProvider[]> {
    try {
      // Try the new status field first
      let q = query(
        collection(db, PROVIDERS_COLLECTION),
        where('status', '==', 'enabled'),
        orderBy('providerName')
      );
      let snapshot = await getDocs(q);

      // If no results with new format, try the old isEnabled field for backward compatibility
      if (snapshot.empty) {
        q = query(
          collection(db, PROVIDERS_COLLECTION),
          where('isEnabled', '==', true),
          orderBy('providerName')
        );
        snapshot = await getDocs(q);
      }

      const providers = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Convert legacy isEnabled to status format for consistency
          status: data.status || (data.isEnabled ? 'enabled' : 'disabled')
        };
      }) as StreamingProvider[];

      return providers;
    } catch (error) {
      return [];
    }
  },

  // Add or update streaming provider
  async upsertProvider(provider: Omit<StreamingProvider, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
    try {
      // Check if provider already exists
      const q = query(
        collection(db, PROVIDERS_COLLECTION),
        where('providerId', '==', provider.providerId)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        // Provider exists - only update metadata, preserve user settings
        const docId = snapshot.docs[0].id;
        const existingData = snapshot.docs[0].data();

        // Only update providerName and logoPath, preserve status and customUrl
        await updateDoc(doc(db, PROVIDERS_COLLECTION, docId), {
          providerName: provider.providerName,
          logoPath: provider.logoPath,
          updatedAt: serverTimestamp(),
          // Preserve existing user settings
          // status and customUrl are NOT updated
        });
      } else {
        // Add new provider - default to NEUTRAL so admin can configure
        await addDoc(collection(db, PROVIDERS_COLLECTION), {
          ...provider,
          status: 'neutral', // New providers are neutral by default
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      throw error;
    }
  },

  // Update provider settings
  async updateProvider(id: string, updates: Partial<StreamingProvider>): Promise<void> {
    try {
      await updateDoc(doc(db, PROVIDERS_COLLECTION, id), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  },

  // Get provider by TMDB ID
  async getProviderByTMDBId(providerId: number): Promise<StreamingProvider | null> {
    try {
      const q = query(
        collection(db, PROVIDERS_COLLECTION),
        where('providerId', '==', providerId)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() } as StreamingProvider;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  // Migrate existing providers from isEnabled to status field (client-side)
  async migrateProvidersToStatusField(): Promise<{ migrated: number; total: number }> {
    try {
      // Get all providers
      const snapshot = await getDocsPreferServer(
        query(collection(db, PROVIDERS_COLLECTION), orderBy('providerName'))
      );

      let migrated = 0;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();

        // Only migrate if it has isEnabled but no status field
        if (typeof data.isEnabled === 'boolean' && !data.status) {
          const newStatus = data.isEnabled ? 'enabled' : 'disabled';

          await updateDoc(doc(db, PROVIDERS_COLLECTION, docSnap.id), {
            status: newStatus,
            updatedAt: serverTimestamp(),
            // Remove the old isEnabled field by setting it to null
            isEnabled: null
          });

          migrated++;
        }
      }

      return { migrated, total: snapshot.docs.length };
    } catch (error) {
      throw error;
    }
  },

  // Seasonal Themes functions
  async getAllSeasonalPeriods() {
    try {
      const periodsRef = collection(db, 'settings', 'seasonalMovies', 'periods');
      const q = query(periodsRef, orderBy('displayOrder', 'asc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      return [];
    }
  },

  async createSeasonalPeriod(periodData: any) {
    try {
      const periodsRef = collection(db, 'settings', 'seasonalMovies', 'periods');
      const docRef = await addDoc(periodsRef, {
        ...periodData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  async updateSeasonalPeriod(id: string, updates: any) {
    try {
      const periodRef = doc(db, 'settings', 'seasonalMovies', 'periods', id);
      await updateDoc(periodRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  },

  async deleteSeasonalPeriod(id: string) {
    try {
      const periodRef = doc(db, 'settings', 'seasonalMovies', 'periods', id);
      await deleteDoc(periodRef);
    } catch (error) {
      throw error;
    }
  }
};